import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Upload as UploadIcon, ArrowBack, Upload } from '@mui/icons-material';
import type { RouteProfile } from '../profileSelector/RouteProfileSelector';
import { DEFAULT_ROUTE_PROFILE } from '../profileSelector/RouteProfileSelector';
import { BikeProfileSelector } from './BikeProfileSelector';
import GPXFileUploader from './GPXFileUploader';
import GPXRoutePreview from './GPXRoutePreview';
import { ThumbnailPreviewModal } from './ThumbnailPreviewModal';
import type { GPXData } from '../../services/gpxProcessor';
import { gpxProcessor } from '../../services/gpxProcessor';
import { RouteStorageManager, StorageType } from '../../services/storage';
import { useAuth } from '../../contexts/AuthContext';
import { calculateRoute } from '../../utils/routeCalculation';
import type { RouteData } from '../../types';
import { RouteThumbnailGenerator } from '../../utils/thumbnailGenerator';
import { AnalyticsService } from '../../services/analytics/HybridAnalyticsService';
import { ElevationThumbnailGenerator } from '../../utils/elevationThumbnailGenerator';

interface GPXUploadModalProps {
  open: boolean;
  onClose: () => void;
  onRouteUploaded: (routeId: string) => void;
}

type UploadStep = 'file' | 'profile' | 'preview' | 'processing' | 'success' | 'error';

export const GPXUploadModal: React.FC<GPXUploadModalProps> = ({
  open,
  onClose,
  onRouteUploaded
}) => {
  const { user, getAccessToken, refreshSession } = useAuth();
  const [currentStep, setCurrentStep] = useState<UploadStep>('file');
  const [selectedProfile, setSelectedProfile] = useState<RouteProfile>(DEFAULT_ROUTE_PROFILE);
  const [gpxData, setGpxData] = useState<GPXData | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null); // Store uploaded file for analytics
  const [error, setError] = useState<string>('');
  const [uploadedRouteId, setUploadedRouteId] = useState<string>('');
  const [finalRouteName, setFinalRouteName] = useState<string>(''); // Track final name if changed
  const [editableRouteName, setEditableRouteName] = useState<string>(''); // Current editable route name
  
  // State for route calculation and thumbnail generation
  const [calculatedRoute, setCalculatedRoute] = useState<RouteData | null>(null);
  const [routeThumbnail, setRouteThumbnail] = useState<string | null>(null);
  const [elevationThumbnail, setElevationThumbnail] = useState<string | null>(null);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [routeCalculationError, setRouteCalculationError] = useState<string>('');

  // State for thumbnail preview modal
  const [thumbnailPreviewOpen, setThumbnailPreviewOpen] = useState(false);

  // Create storage manager
  const storageManager = React.useMemo(() => new RouteStorageManager({
    type: StorageType.SUPABASE,
    endpoint: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  }, {
    getAccessToken,
    refreshSession
  }), [getAccessToken, refreshSession]);

  const handleClose = () => {
    // If we're on the success step and have an uploaded route, refresh the routes list
    if (currentStep === 'success' && uploadedRouteId) {
      // Call onRouteUploaded to refresh the routes list, but don't set sessionStorage
      // This way the MyRoutesPage will refresh the list but won't navigate to cycling map
      onRouteUploaded(uploadedRouteId);
    }
    
    // Reset state
    setCurrentStep('file');
    setSelectedProfile(DEFAULT_ROUTE_PROFILE);
    setGpxData(null);
    setError('');
    setUploadedRouteId('');
    setEditableRouteName(''); // Clear editable route name
    setCalculatedRoute(null);
    setRouteThumbnail(null);
    setElevationThumbnail(null);
    setIsCalculatingRoute(false);
    setRouteCalculationError('');
    setThumbnailPreviewOpen(false); // Reset thumbnail preview state
    onClose();
  };

  const handleThumbnailClick = () => {
    if (routeThumbnail) {
      setThumbnailPreviewOpen(true);
    }
  };

  const handleThumbnailPreviewClose = () => {
    setThumbnailPreviewOpen(false);
  };

  const handleFileSelected = (file: File, data: GPXData) => {
    setGpxData(data);
    setUploadedFile(file); // Store file for later analytics tracking
    setEditableRouteName(data.name); // Initialize editable name with GPX name
    // Clear previous route calculation data when new file is selected
    setCalculatedRoute(null);
    setRouteThumbnail(null);
    setElevationThumbnail(null);
    setIsCalculatingRoute(false);
    setRouteCalculationError('');
    setCurrentStep('profile');
  };

  const handleBackToProfile = () => {
    // Clear route calculation data when going back to profile selection
    setCalculatedRoute(null);
    setRouteThumbnail(null);
    setElevationThumbnail(null);
    setIsCalculatingRoute(false);
    setRouteCalculationError('');
    setCurrentStep('profile');
  };
  const reduceWaypoints = (waypoints: {lat: number, lng: number}[], maxPoints: number = 25): {lat: number, lng: number}[] => {
    if (waypoints.length <= maxPoints) {
      return waypoints;
    }
    
    const result: {lat: number, lng: number}[] = [];
    const step = Math.floor(waypoints.length / (maxPoints - 1));

    // Always include start point
    result.push(waypoints[0]);

    // Extract points at regular intervals
    for (let i = step; i < waypoints.length - step; i += step) {
      result.push(waypoints[i]);
      if (result.length >= maxPoints - 1) break;
    }

    // Always include end point
    if (result.length === 0 || result[result.length - 1] !== waypoints[waypoints.length - 1]) {
      result.push(waypoints[waypoints.length - 1]);
    }

    return result;
  };

  const handleProfileSelected = async (profile: RouteProfile) => {
    setSelectedProfile(profile);
    setCurrentStep('preview');
    
    // Start route calculation and thumbnail generation
    if (gpxData && gpxData.waypoints.length >= 2) {
      setIsCalculatingRoute(true);
      setRouteCalculationError('');
      
      try {
        // Reduce waypoints to a manageable number before API call
        const originalWaypoints = gpxData.waypoints.map(wp => ({ lat: wp.lat, lng: wp.lng }));
        const reducedWaypoints = reduceWaypoints(originalWaypoints, 25);
                
        const routeData = await calculateRoute(
          reducedWaypoints,
          profile,
          'geojson',
          undefined,
          { getAccessToken, refreshSession }
        );
        
        if (routeData) {
          setCalculatedRoute(routeData);
          
          // Track GPX upload analytics now that we have the bike profile
          try {
            if (uploadedFile && gpxData) {
              AnalyticsService.trackGPXUploaded({
                filename: uploadedFile.name, // Used only locally, not stored
                fileSizeBytes: uploadedFile.size,
                pointsCount: gpxData.metadata.totalPoints,
                bikeProfile: profile
              });
            }
          } catch (analyticsError) {
            console.warn('Failed to track GPX upload analytics:', analyticsError);
          }
          
          // Generate route thumbnail using route coordinates
          try {
            const routeThumbnailBlob = await RouteThumbnailGenerator.generateThumbnail(
              routeData.coordinates
            );
            // Convert blob to data URL for display
            const reader = new FileReader();
            reader.onload = () => {
              setRouteThumbnail(reader.result as string);
            };
            reader.readAsDataURL(routeThumbnailBlob);
          } catch (thumbnailError) {
            console.warn('Failed to generate route thumbnail:', thumbnailError);
            // Don't block the flow if thumbnail generation fails
          }

          // Generate elevation thumbnail if elevation data is available
          if (routeData.elevationData && routeData.elevationData.length > 0) {
            try {
              const elevationThumbnailBlob = await ElevationThumbnailGenerator.generateThemedThumbnail(
                routeData.elevationData,
                profile,
                'medium'
              );
              // Convert blob to data URL for display
              const elevationReader = new FileReader();
              elevationReader.onload = () => {
                setElevationThumbnail(elevationReader.result as string);
              };
              elevationReader.readAsDataURL(elevationThumbnailBlob);
            } catch (elevationThumbnailError) {
              console.warn('Failed to generate elevation thumbnail:', elevationThumbnailError);
              // Don't block the flow if elevation thumbnail generation fails
            }
          }
        }
      } catch (error) {
        console.error('Failed to calculate route:', error);
        setRouteCalculationError('Failed to calculate route preview');
      } finally {
        setIsCalculatingRoute(false);
      }
    }
  };

  const handleConfirmUpload = async () => {
    if (!gpxData || !user) {
      setError('Missing required data for upload');
      return;
    }
    
    setCurrentStep('processing');
    setError('');

    try {
  
      // Step 1: Process GPX through routing API
      const result = await gpxProcessor.processGPXThroughRoutingAPI(
        gpxData, 
        selectedProfile, 
        user.id
      );

      if (!result.success || !result.route) {
        // Fallback: Try direct import if routing API fails
        console.warn('Routing API failed, falling back to direct import:', result.error);
        const fallbackResult = await gpxProcessor.importGPXDirectly(
          gpxData, 
          selectedProfile, 
          user.id
        );
        
        if (!fallbackResult.success || !fallbackResult.route) {
          throw new Error(fallbackResult.error || 'Failed to process GPX file');
        }
        
        result.route = fallbackResult.route;
      }

      // Step 2: Update route name with user's custom name if different
      if (editableRouteName && editableRouteName.trim() && editableRouteName !== result.route.name) {
        result.route.name = editableRouteName.trim();
      }

      // Step 3: Save route to database
      const saveResult = await storageManager.saveRoute(result.route);
      
      if (!saveResult.success || !saveResult.data?.id) {
        throw new Error(saveResult.error || 'Failed to save route');
      }

      // Step 4: Save thumbnails if available
      const thumbnailUpdates: { thumbnail_path?: string; elevation_thumbnail_path?: string } = {};
      
      // Upload route thumbnail
      if (routeThumbnail && saveResult.data.id) {
        try {
          // Convert data URL to blob
          const response = await fetch(routeThumbnail);
          const blob = await response.blob();
          
          // Upload thumbnail directly to Supabase storage
          const filename = `route-${saveResult.data.id}-${Date.now()}.png`;
          const formData = new FormData();
          formData.append('file', blob, filename);
          
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
          
          // Get access token for authenticated storage upload
          const accessToken = await getAccessToken();
          const authToken = accessToken || anonKey;
          
          const uploadResponse = await fetch(`${supabaseUrl}/storage/v1/object/route-thumbnails/${filename}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'apikey': anonKey
            },
            body: formData
          });

          if (uploadResponse.ok) {
            thumbnailUpdates.thumbnail_path = filename;
          } else {
            console.warn('Failed to upload route thumbnail:', uploadResponse.status);
          }
        } catch (thumbnailError) {
          console.warn('Failed to save route thumbnail:', thumbnailError);
          // Don't fail the entire upload if thumbnail save fails
        }
      }

      // Upload elevation thumbnail
      if (elevationThumbnail && saveResult.data.id) {
        try {
          // Convert data URL to blob
          const elevationResponse = await fetch(elevationThumbnail);
          const elevationBlob = await elevationResponse.blob();
          
          // Upload elevation thumbnail directly to Supabase storage
          const elevationFilename = `elevation-${saveResult.data.id}-${Date.now()}.png`;
          const elevationFormData = new FormData();
          elevationFormData.append('file', elevationBlob, elevationFilename);
          
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
          
          // Get access token for authenticated storage upload
          const accessToken = await getAccessToken();
          const authToken = accessToken || anonKey;
          
          const elevationUploadResponse = await fetch(`${supabaseUrl}/storage/v1/object/elevation-thumbnails/${elevationFilename}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'apikey': anonKey
            },
            body: elevationFormData
          });

          if (elevationUploadResponse.ok) {
            thumbnailUpdates.elevation_thumbnail_path = elevationFilename;
          } else {
            console.warn('Failed to upload elevation thumbnail:', elevationUploadResponse.status);
          }
        } catch (elevationThumbnailError) {
          console.warn('Failed to save elevation thumbnail:', elevationThumbnailError);
          // Don't fail the entire upload if elevation thumbnail save fails
        }
      }

      // Update route with thumbnail paths if any were uploaded successfully
      if (Object.keys(thumbnailUpdates).length > 0) {
        try {
          await storageManager.updateRoute(saveResult.data.id, thumbnailUpdates);
        } catch (updateError) {
          console.warn('Failed to update route with thumbnail paths:', updateError);
          // Don't fail the entire upload if thumbnail path update fails
        }
      }
      
      setUploadedRouteId(saveResult.data.id);
      setFinalRouteName(saveResult.data.name); // Capture the final name (may be different if auto-incremented)
      setCurrentStep('success');
      
    } catch (err) {
      console.error('GPX upload failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to process GPX file');
      setCurrentStep('error');
    }
  };

  const handleOK = () => {
    // Trigger route list refresh without navigation
    if (uploadedRouteId) {
      // Call onRouteUploaded to refresh the routes list, but don't set sessionStorage
      // This way the MyRoutesPage will refresh the list but won't navigate to cycling map
      onRouteUploaded(uploadedRouteId);
    }
    handleClose();
  };

  const handleFinish = async () => {
    if (uploadedRouteId) {
      // Directly navigate to the route path instead of using sessionStorage
      // This is cleaner and will trigger the URL-based route loading with proper loading states
      onRouteUploaded(uploadedRouteId);
    }
    handleClose();
  };

  const getStepIndex = () => {
    switch (currentStep) {
      case 'file': return 0;
      case 'profile': return 1;
      case 'preview': return 2;
      case 'processing': return 2;
      case 'success': return 3;
      case 'error': return 2;
      default: return 0;
    }
  };

  const steps = ['Choose GPX File', 'Select Bike Profile', 'Preview Route', 'Complete'];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disableRestoreFocus={true}
        PaperProps={{
          sx: { minHeight: '500px' }
        }}
      >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon color="primary" fontSize="small" />
            Upload GPX Route
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ paddingBottom: 0 }}>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={getStepIndex()} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {currentStep === 'file' && (
          <GPXFileUploader
            onFileUploaded={handleFileSelected}
          />
        )}

        {currentStep === 'profile' && gpxData && (
          <Box>
            <BikeProfileSelector
              selectedProfile={selectedProfile}
              onProfileChange={setSelectedProfile}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button 
                onClick={() => setCurrentStep('file')}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>
              <Button 
                onClick={() => handleProfileSelected(selectedProfile)}
                variant="contained"
                disabled={!selectedProfile}
              >
                Next: Preview Route
              </Button>
            </Box>
          </Box>
        )}

        {currentStep === 'preview' && gpxData && (
          <Box>
            <GPXRoutePreview
              gpxData={gpxData}
              selectedProfile={selectedProfile}
              calculatedRoute={calculatedRoute}
              routeThumbnail={routeThumbnail}
              isCalculatingRoute={isCalculatingRoute}
              routeCalculationError={routeCalculationError}
              routeName={editableRouteName}
              onRouteNameChange={setEditableRouteName}
              onThumbnailClick={handleThumbnailClick}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button 
                onClick={handleBackToProfile}
                startIcon={<ArrowBack />}
                disabled={isCalculatingRoute}
                variant="outlined"
              >
                Back
              </Button>
              <Button 
                onClick={handleConfirmUpload}
                variant="contained"
                startIcon={<Upload />}
                disabled={isCalculatingRoute || !calculatedRoute || !!routeCalculationError}
              >
                Upload Route
              </Button>
            </Box>
            
            {routeCalculationError && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="error">
                  Please go back and try a different profile or check your GPX file
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {currentStep === 'processing' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Uploading and Saving Route
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Processing your route and saving to your collection...
            </Typography>
          </Box>
        )}

        {currentStep === 'success' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5 }}>
            <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="body1">
                Route "{finalRouteName || editableRouteName}" has been successfully uploaded and saved!
              </Typography>
            </Alert>

            {/* Show route summary from previous step */}
            {gpxData && calculatedRoute && (
              <Box sx={{ width: '100%', mb: 1 }}>
                <GPXRoutePreview
                  gpxData={gpxData}
                  selectedProfile={selectedProfile}
                  calculatedRoute={calculatedRoute}
                  routeThumbnail={routeThumbnail}
                  isCalculatingRoute={false}
                  routeCalculationError=""
                  routeName={finalRouteName || editableRouteName}
                  onRouteNameChange={() => {}} // Disabled in success step
                  onThumbnailClick={handleThumbnailClick}
                  readOnly={true}
                />
              </Box>
            )}
            
            {/* Buttons with consistent spacing like other steps */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 2, gap: 2 }}>
              <Button 
                onClick={handleOK} 
                variant="outlined"
              >
                Close
              </Button>
              <Button 
                onClick={handleFinish} 
                variant="contained"
                color="primary"
              >
                View Route
              </Button>
            </Box>
          </Box>
        )}

        {currentStep === 'error' && (
          <Box sx={{ py: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to process GPX file
            </Alert>
            <Typography variant="body2">
              Please try again or contact support if the problem persists.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
        {currentStep === 'error' && (
          <>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button onClick={() => setCurrentStep('preview')} variant="contained">
              Try Again
            </Button>
          </>
        )}
      </DialogActions>
      </Dialog>

      {/* Thumbnail Preview Modal */}
      <ThumbnailPreviewModal
        open={thumbnailPreviewOpen}
        onClose={handleThumbnailPreviewClose}
        thumbnailUrl={routeThumbnail}
        routeName={editableRouteName || 'Route'}
      />
    </>
  );
};
